(() => {
  if (customElements.get('orion-mattress-gallery')) return;
  // A shared cadence for every mattress and topper, independent of image count.
  const HOLD_MS = 4000;
  const TRANSITION_MS = 900;
  const PASS_MS = 1150; // the pass of light between images on the refreshed page
  const INPUT_SETTLE_MS = 140;
  class OrionMattressGallery extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._abort = new AbortController();
      this._reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this._views = Array.from(this.querySelectorAll('[data-view]'));
      this._buttons = Array.from(this.querySelectorAll('[data-select]'));
      this._failed = new Set();
      this._index = 0;
      this._request = (this._request || 0) + 1;
      this._moving = false;
      this._shown = false;
      this._pending = null;
      this._destination = null;
      this._allowMotion = true;
      this.setAttribute('data-motion-enabled', '');
      // Autoplay always loops; there is no pause. It only waits while the gallery is off screen,
      // in full screen or while the Roll Pack carton is being turned.
      this._demoEnabled = true;
      this._demoDueAt = null;
      this._presentationHolds = new Set();
      this._heldRemaining = null;
      this._viewerPaused = false;
      this._gesture = null;
      this._animations = [];
      this._progress = this.querySelector('[data-gallery-progress]');
      this._progressDueAt = null;
      this._viewer = this.querySelector('orion-image-viewer');
      this._viewer?.attach?.(this);
      const signal = this._abort.signal;
      this._warmGeneration = 0;
      const prime = event => {
        const button = event.target.closest('[data-select]');
        if (button) this.load(Number(button.dataset.select), 'high').catch(() => {});
      };
      this.addEventListener('pointerover', prime, { signal });
      this.addEventListener('focusin', prime, { signal });
      this.addEventListener('click', event => {
        const selection = event.target.closest('[data-select]');
        if (event.target.closest('[data-gallery-retry]')) { this.retry(); return; }
        if (selection) this.goTo(Number(selection.dataset.select));
        else if (event.target.closest('[data-gallery-zoom]')) this.openZoom();
      }, { signal });
      this.addEventListener('keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey || !this._shown) return;
        const keys = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
        if (event.key in keys) { event.preventDefault(); this.step(keys[event.key]); }
        else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          const indices = this.usableIndices();
          this.goTo(event.key === 'Home' ? indices[0] : indices[indices.length - 1], event.key === 'Home' ? -1 : 1);
        }
      }, { signal });
      this.addEventListener('pointerdown', event => {
        if (event.isPrimary === false || event.button !== 0 || event.target.closest('button, a') || !this._shown) return;
        this._gesture = { x: event.clientX, y: event.clientY, id: event.pointerId, time: performance.now() };
        this.setPointerCapture?.(event.pointerId);
      }, { signal });
      this.addEventListener('pointerup', event => {
        const gesture = this._gesture;
        this._gesture = null;
        if (!gesture || gesture.id !== event.pointerId) return;
        const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
        if (Math.abs(dx) >= 45 && Math.abs(dx) > Math.abs(dy) * 1.3 && performance.now() - gesture.time < 1600) {
          const direction = dx < 0 ? 1 : -1;
          if (!(gesture.autoStepped && direction === 1)) this.step(direction);
        }
        this.syncDemo();
      }, { signal });
      this.addEventListener('pointercancel', () => { this._gesture = null; this.syncDemo(); }, { signal });
      this.addEventListener('lostpointercapture', () => { this._gesture = null; this.syncDemo(); }, { signal });
      // Autoplay always keeps playing: neither focus inside the gallery nor a press on it pauses it.
      document.addEventListener('visibilitychange', () => this.syncDemo(), { signal });
      document.addEventListener('orion:page-ready', () => this.syncDemo(), { signal });
      window.addEventListener('pageshow', () => this.syncDemo(), { signal });
      if ('IntersectionObserver' in window) {
        this._visibilityObserver = new IntersectionObserver(() => this.syncDemo(), { threshold: [0, .1] });
        this._visibilityObserver.observe(this);
      } else {
        window.addEventListener('scroll', () => this.syncDemo(), { passive: true, signal });
      }
      this.initialize();
    }

    disconnectedCallback() {
      this._connected = false;
      this._viewer?.teardown?.();
      this._abort?.abort();
      this._request++;
      this._animations?.forEach(animation => animation.cancel());
      this._animations = [];
      this._moving = false;
      this._destination = null;
      this._pending = null;
      delete this.dataset.transitioning;
      this._views.forEach(view => view.classList.remove('is-moving'));
      this.cancelWarm();
      this.clearDemoTimer();
      this.clearProgress();
      this._visibilityObserver?.disconnect();
    }

    // A view waiting hidden (a colour's own picture while another colour is chosen, the gallery snippet's colour_views)
    // is left out like one that failed: no number, no swipe, no autoplay, not in the viewer.
    usableIndices() {
      return this._views.map((_, index) => index).filter(index => !this._failed.has(index) && !this._views[index].hidden);
    }

    async initialize() {
      this.syncControls();
      if (!this._views.length) { this.showEmpty(false); return; }
      // On the refreshed page a phone shows the pictures across the whole width (orion-mattress-gallery.css); told so,
      // the browser picks copies sharp enough for it. Pictures whose sizes already say so (the gallery snippet) are left
      // alone: changing them would ask for the same picture a second time.
      if (this.closest('.orion-mattress-product--refresh') && window.matchMedia('(max-width: 749px)').matches) {
        this._views.forEach(view => {
          const image = view.querySelector('img');
          if (image && !(image.getAttribute('sizes') || '').startsWith('(max-width: 749px)')) image.sizes = view.dataset.kind === 'detail' ? '228vw' : '100vw';
        });
      }
      this.dataset.state = 'loading';
      this.setAttribute('aria-busy', 'true');
      const request = ++this._request;
      // A page opened on a variant may start on that variant's own picture (data-start, the gallery snippet).
      const start = Number(this.dataset.start) || 0;
      const usable = this.usableIndices();
      for (const index of usable.includes(start) ? [start, ...usable.filter(index => index !== start)] : usable) {
        try { await this.load(index); }
        catch (_) {
          if (!this.isConnected || this._abort.signal.aborted) return;
          this._failed.add(index);
          this._views[index].classList.remove('is-current');
          this._views[index].setAttribute('aria-hidden', 'true');
          continue;
        }
        if (request !== this._request || !this.isConnected) return;
        this._index = index;
        this._shown = true;
        this._views.forEach((view, i) => {
          view.classList.toggle('is-current', i === index);
          view.setAttribute('aria-hidden', String(i !== index));
        });
        this.dataset.kind = this._views[index].dataset.kind || 'image';
        this.dataset.state = 'ready';
        this.setAttribute('data-ready', '');
        this.removeAttribute('aria-busy');
        this.querySelector('[data-gallery-empty]').hidden = true;
        this.syncControls();
        this.warm();
        return;
      }
      if (this.isConnected) this.showEmpty(true);
    }

    showEmpty(failed) {
      this._shown = false;
      this.clearProgress();
      this.clearDemoTimer();
      this.removeAttribute('data-ready');
      this.removeAttribute('aria-busy');
      this.dataset.state = failed ? 'error' : 'empty';
      this._views.forEach(view => { view.classList.remove('is-current', 'is-moving'); view.setAttribute('aria-hidden', 'true'); });
      const empty = this.querySelector('[data-gallery-empty]');
      empty.hidden = false;
      if (failed) this.querySelector('[data-gallery-empty-label]').textContent = this.dataset.errorLabel;
      this.querySelector('[data-gallery-retry]').hidden = !failed;
      this.syncControls();
    }

    retry() {
      if (this.dataset.state !== 'error') return;
      this._failed.clear();
      this._views.forEach(view => {
        const image = view.querySelector('img');
        image._ready = false;
        image.removeAttribute('src');
        image.removeAttribute('srcset');
      });
      this.querySelector('[data-gallery-retry]').hidden = true;
      this.initialize();
    }

    async load(index, priority = 'high') {
      const image = this._views[index]?.querySelector('img');
      if (!image) throw new Error('Image unavailable');
      image.fetchPriority = priority;
      if (image._ready) return;
      if (image._loading) return image._loading;
      image._loading = (async () => {
        await new Promise((resolve, reject) => {
          const done = error => {
            clearTimeout(timer);
            image.removeEventListener('load', onLoad);
            image.removeEventListener('error', onError);
            this._abort.signal.removeEventListener('abort', onAbort);
            error ? reject(error) : resolve();
          };
          const onLoad = () => image.naturalWidth ? done() : onError();
          const onError = () => done(new Error('Image unavailable'));
          const onAbort = () => done(new Error('Gallery disconnected'));
          const timer = setTimeout(onError, 15000);
          image.addEventListener('load', onLoad, { once: true });
          image.addEventListener('error', onError, { once: true });
          this._abort.signal.addEventListener('abort', onAbort, { once: true });
          if (!image.getAttribute('src')) {
            image.srcset = image.dataset.srcset || '';
            image.src = image.dataset.src;
          }
          if (image.complete) onLoad();
        });
        if (image.decode) {
          try { await image.decode(); }
          catch (error) { if (!image.complete || !image.naturalWidth) throw error; }
        }
        image._ready = true;
      })().finally(() => { image._loading = null; });
      return image._loading;
    }

    cancelWarm() {
      this._warmGeneration++;
      clearTimeout(this._warmTimer);
      if (this._warmIdle != null && 'cancelIdleCallback' in window) cancelIdleCallback(this._warmIdle);
      this._warmIdle = null;
    }

    warm() {
      this.cancelWarm();
      if (navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) return;
      const generation = this._warmGeneration;
      this._warmTimer = setTimeout(() => {
        const prepare = () => {
          this._warmIdle = null;
          if (generation !== this._warmGeneration || !this.isConnected || document.hidden || this._moving || this._viewerPaused) return;
          const rect = this.getBoundingClientRect();
          if (rect.bottom <= 0 || rect.top >= innerHeight) return;
          const indices = this.usableIndices();
          if (indices.length < 2) return;
          const next = indices[(indices.indexOf(this._index) + 1) % indices.length];
          this.load(next, 'low').catch(() => {});
        };
        if ('requestIdleCallback' in window) this._warmIdle = requestIdleCallback(prepare, { timeout: 1000 });
        else prepare();
      }, 250);
    }

    syncControls() {
      const indices = this.usableIndices();
      const count = indices.length;
      const position = Math.max(0, indices.indexOf(this._index));
      this.dataset.count = String(count);
      this.dataset.index = String(position);
      this.style.setProperty('--view-count', String(count));
      this.style.setProperty('--view-index', String(position));
      // The numbers show from two pictures up, or from data-numbers-from (the materials' colours: three).
      const least = Number(this.dataset.numbersFrom) || 2;
      this.querySelectorAll('[data-gallery-multiple]').forEach(element => { element.hidden = count < least; });
      this.querySelector('[data-gallery-zoom]').hidden = !this._shown;
      // The count reads "this image — next image": the line fills towards the next one, so after the last it is 01.
      this.querySelector('[data-current-number]').textContent = String(position + 1).padStart(2, '0');
      this.querySelector('[data-next-number]').textContent = String(count ? (position + 1) % count + 1 : 1).padStart(2, '0');
      this._buttons.forEach((button, index) => {
        const rank = indices.indexOf(index);
        button.hidden = rank < 0;
        button.setAttribute('aria-pressed', String(index === this._index && this._shown));
        if (index === this._index && this._shown) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
        if (rank >= 0) {
          button.textContent = String(rank + 1).padStart(2, '0');
          button.setAttribute('aria-label', this.dataset.loadLabel.replace('__INDEX__', String(rank + 1)));
          this._views[index].setAttribute('aria-label', this.dataset.imageLabel.replace('__INDEX__', String(rank + 1)));
        }
      });
      this.querySelector('[data-gallery-zoom]').setAttribute('aria-label', this.dataset.zoomLabel.replace('__INDEX__', String(position + 1)));
      const active = this._buttons[this._index];
      const rail = this.querySelector('.orion-mattress-gallery__rail');
      if (active && rail && count > 1) {
        if (active.offsetTop < rail.scrollTop) rail.scrollTop = active.offsetTop;
        else if (active.offsetTop + active.offsetHeight > rail.scrollTop + rail.clientHeight) rail.scrollTop = active.offsetTop + active.offsetHeight - rail.clientHeight;
      }
      this.syncDemo();
      this._viewer?.syncAvailability?.();
    }

    step(direction) {
      const indices = this.usableIndices();
      if (indices.length < 2 || !this._shown) return;
      const base = this._pending?.index ?? this._destination ?? this._index;
      const position = indices.indexOf(base);
      this.goTo(indices[(position + direction + indices.length) % indices.length], direction);
    }

    // The band of light that passes between images on the refreshed page, made once, above the images and below the
    // controls (assets/orion-mattress-refresh.css).
    passBand() {
      if (!this._pass?.isConnected) {
        this._pass = document.createElement('div');
        this._pass.className = 'orion-mattress-gallery__pass';
        this._pass.setAttribute('aria-hidden', 'true');
        this.querySelector('.orion-mattress-gallery__viewport')?.after(this._pass);
      }
      return this._pass;
    }

    async goTo(index, direction, instant = false) {
      if (!this._shown || !Number.isInteger(index) || index < 0 || index >= this._views.length || this._failed.has(index)) return;
      this.cancelWarm();
      if (this._moving) {
        // Keep only the latest choice and finish the current movement promptly,
        // without jumping to its last frame or building a queue of stale clicks.
        this._pending = index === this._destination ? null : { index, direction, instant };
        this.dataset.index = String(this.usableIndices().indexOf(index));
        this.style.setProperty('--view-index', this.dataset.index);
        this.showNumber(index);
        if (this._pending) {
          this.load(index).catch(() => {});
          this._animations.forEach(animation => {
            const duration = Number(animation.effect?.getTiming?.().duration) || TRANSITION_MS;
            const remaining = Math.max(0, duration - Number(animation.currentTime || 0));
            animation.updatePlaybackRate(Math.max(1, remaining / INPUT_SETTLE_MS));
          });
        }
        return;
      }
      this.clearDemoTimer();
      this._demoDueAt = null;
      this._heldRemaining = null;
      this.pauseProgress();
      const request = ++this._request;
      if (index === this._index) { this._destination = null; this.removeAttribute('aria-busy'); this.syncDemo(); return; }
      // Track intent during decoding too, so repeated Next/Previous advances.
      this._destination = index;
      this.setAttribute('aria-busy', 'true');
      try { await this.load(index); }
      catch (_) {
        if (request !== this._request || !this.isConnected) return;
        this._failed.add(index);
        this._destination = null;
        this.removeAttribute('aria-busy');
        this.syncControls();
        this.querySelector('.orion-mattress-gallery__announcement').textContent = this.dataset.errorLabel;
        return;
      }
      if (request !== this._request || !this.isConnected) return;
      this.removeAttribute('aria-busy');
      this._moving = true;
      this._destination = index;
      direction = direction || (index > this._index ? 1 : -1);
      const outgoing = this._views[this._index];
      const incoming = this._views[index];
      this.dataset.index = String(this.usableIndices().indexOf(index));
      this.dataset.kind = incoming.dataset.kind || 'image';
      this.style.setProperty('--view-index', this.dataset.index);
      outgoing.classList.add('is-moving');
      incoming.classList.add('is-moving');
      this.dataset.transitioning = 'true';
      // The count moves on with the image: never "04 — 04" while the first image comes back.
      this.showNumber(index);
      this.finishProgress();
      if (!instant) {
        const hasLight = view => !['cutaway', 'diagram'].includes(view.dataset.kind);
        const lightFrom = hasLight(outgoing) ? 1 : 0;
        const lightTo = hasLight(incoming) ? 1 : 0;
        const light = this.querySelector('.orion-mattress-gallery__light');
        if (this.closest('.orion-mattress-product--refresh')) {
          // A pass of light (the refreshed page): a soft band of white light crosses the picture in the direction of
          // travel, and the change happens under its brightest part: the shown image lets go into the light and the
          // next one comes out of it and settles. Only transforms and opacity, so it all runs on the compositor.
          // The band crosses the middle at about half time; the shown image fades out as it arrives and the next
          // one fades in as it leaves, so the two meet under it.
          const options = { duration: PASS_MS, easing: 'linear', fill: 'both' };
          const pass = this.passBand();
          pass.style.left = direction > 0 ? '0' : 'auto';
          pass.style.right = direction > 0 ? 'auto' : '0';
          const across = direction > 0 ? ['-100%', '210%'] : ['100%', '-210%'];
          const dim = .35 * Math.max(lightFrom, lightTo);
          this._animations = [
            outgoing.animate([
              { offset: 0, transform: 'translate3d(0,0,0) scale(1)', opacity: 1, easing: 'cubic-bezier(.3,0,.5,1)' },
              { offset: .24, opacity: 1, easing: 'cubic-bezier(.45,0,.55,1)' },
              { offset: .52, opacity: 0 },
              { offset: 1, transform: `translate3d(${direction * -3}%,0,0) scale(.98)`, opacity: 0 }
            ], options),
            incoming.animate([
              { offset: 0, transform: `translate3d(${direction * 3}%,0,0) scale(1.02)`, opacity: 0 },
              { offset: .42, transform: `translate3d(${direction * 2.4}%,0,0) scale(1.016)`, opacity: 0, easing: 'cubic-bezier(.22,.61,.36,1)' },
              { offset: .8, opacity: 1 },
              { offset: 1, transform: 'translate3d(0,0,0) scale(1)', opacity: 1 }
            ], options),
            pass.animate([
              { offset: 0, transform: `translate3d(${across[0]},0,0)`, opacity: 0, easing: 'cubic-bezier(.45,.05,.55,.95)' },
              { offset: .15, opacity: 1 },
              { offset: .82, opacity: 1 },
              { offset: 1, transform: `translate3d(${across[1]},0,0)`, opacity: 0 }
            ], options),
            light.animate([
              { offset: 0, opacity: lightFrom }, { offset: .42, opacity: dim },
              { offset: .56, opacity: dim }, { offset: 1, opacity: lightTo }
            ], options)
          ];
        } else {
          const options = { duration: TRANSITION_MS, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'both' };
          // Drift and dissolve: the shown image recedes and fades out to one side while the next one glides in
          // from the other and settles, using only compositor properties. Like the autoplay, it runs whatever the
          // motion preference (the owner's rule). Lighting dims separately instead of repainting each image.
          const outgoingFrames = [
            { offset: 0, transform: 'translate3d(0,0,0) scale(1)', opacity: 1 },
            { offset: .55, transform: `translate3d(${direction * -5}%,0,0) scale(.96)`, opacity: 0 },
            { offset: 1, transform: `translate3d(${direction * -7}%,0,0) scale(.95)`, opacity: 0 }
          ];
          const incomingFrames = [
            { offset: 0, transform: `translate3d(${direction * 9}%,0,0) scale(1.04)`, opacity: 0 },
            { offset: .3, transform: `translate3d(${direction * 6}%,0,0) scale(1.028)`, opacity: 0 },
            { offset: 1, transform: 'translate3d(0,0,0) scale(1)', opacity: 1 }
          ];
          this._animations = [
            outgoing.animate(outgoingFrames, options),
            incoming.animate(incomingFrames, options),
            light.animate([
              { offset: 0, opacity: lightFrom }, { offset: .3, opacity: 0 },
              { offset: .52, opacity: 0 }, { offset: 1, opacity: lightTo }
            ], options)
          ];
        }
        await Promise.all(this._animations.map(animation => animation.finished.catch(() => {})));
      }
      if (!this.isConnected || request !== this._request) return;
      outgoing.classList.remove('is-current', 'is-moving');
      outgoing.setAttribute('aria-hidden', 'true');
      incoming.classList.add('is-current');
      incoming.classList.remove('is-moving');
      incoming.setAttribute('aria-hidden', 'false');
      this._animations.forEach(animation => animation.cancel());
      this._animations = [];
      this._index = index;
      this._destination = null;
      this._moving = false;
      delete this.dataset.transitioning;
      this.syncControls();
      if (!this._demoEnabled) this.querySelector('.orion-mattress-gallery__announcement').textContent = incoming.getAttribute('aria-label');
      this.warm();
      if (this._pending) {
        const pending = this._pending;
        this._pending = null;
        this.goTo(pending.index, pending.direction, pending.instant);
      }
    }

    clearProgress() {
      this._progressAnimation?.cancel();
      this._progressAnimation = null;
      this._progressDueAt = null;
      if (this._progress) this._progress.dataset.state = 'idle';
    }

    pauseProgress() {
      if (this._progress?.dataset.state === 'finishing') return;
      this._progressAnimation?.pause();
      this._progressDueAt = null;
      if (this._progress) this._progress.dataset.state = 'paused';
    }

    finishProgress() {
      if (!this._progress) return;
      const matrix = getComputedStyle(this._progress).transform;
      const filled = matrix && matrix.startsWith('matrix(') ? Math.max(0, Math.min(1, Number(matrix.slice(7).split(',')[0]) || 0)) : 0;
      this._progressAnimation?.cancel();
      this._progressDueAt = null;
      this._progress.dataset.state = 'finishing';
      this._progressAnimation = this._progress.animate([
        { transform: 'translateX(0) scaleX(' + filled + ')' },
        { transform: 'translateX(100%) scaleX(0)' }
      ], { duration: Math.min(520, TRANSITION_MS), easing: 'cubic-bezier(.4,0,.2,1)', fill: 'forwards' });
    }

    syncProgress() {
      if (!this._progress || this._progressDueAt === this._demoDueAt) return;
      this.clearProgress();
      const remaining = Math.max(0, Math.min(HOLD_MS, this._demoDueAt - performance.now()));
      const elapsed = 1 - remaining / HOLD_MS;
      this._progressDueAt = this._demoDueAt;
      this._progress.dataset.state = 'running';
      this._progress.dataset.durationMs = String(HOLD_MS);
      this._progressAnimation = this._progress.animate([
        { transform: 'scaleX(' + elapsed + ')' },
        { transform: 'scaleX(1)' }
      ], { duration: remaining, easing: 'linear', fill: 'forwards' });
    }

    showNumber(index) {
      const indices = this.usableIndices();
      const rank = indices.indexOf(index);
      if (rank < 0) return;
      this._buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
      // This image and the next one (after the last: 01), each rising in as it changes.
      const texts = [rank + 1, (rank + 1) % indices.length + 1].map(number => String(number).padStart(2, '0'));
      [this.querySelector('[data-current-number]'), this.querySelector('[data-next-number]')].forEach((target, i) => {
        if (!target || target.textContent === texts[i]) return;
        target.textContent = texts[i];
        target.animate?.([{ opacity: 0, transform: 'translateY(45%)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 420, delay: i * 70, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'backwards' });
      });
    }

    clearDemoTimer() {
      clearTimeout(this._demoTimer);
      this._demoTimer = null;
    }

    canDemoRun() {
      const rect = this.getBoundingClientRect();
      const visibleHeight = Math.min(rect.bottom, innerHeight) - Math.max(rect.top, 0);
      return this.isConnected && this._shown && this.usableIndices().length > 1 &&
        this._demoEnabled && !document.hidden && !document.documentElement?.hasAttribute('data-orion-page-loading') && !this._viewerPaused && !this._presentationHolds.size &&
        visibleHeight >= Math.min(rect.height, innerHeight) * .1 && rect.right > 0 && rect.left < innerWidth;
    }

    syncDemo() {
      this.clearDemoTimer();
      const available = this._shown && this.usableIndices().length > 1;
      const enabled = available && this._demoEnabled;
      const running = this.canDemoRun();
      this.dataset.autoplay = !available ? 'unavailable' : !enabled ? 'paused' : running ? 'running' : 'waiting';
      if (!running) { this._demoDueAt = null; if (available) this.pauseProgress(); else this.clearProgress(); return; }
      if (this._moving || this.hasAttribute('aria-busy')) { this.pauseProgress(); return; }
      // Preserve the deadline when visibility/focus observers repeat a running state.
      // A completed transition (or a genuine resume) starts a fresh reading interval.
      if (this._demoDueAt === null) {
        this._demoDueAt = performance.now() + (this._heldRemaining ?? HOLD_MS);
        this._heldRemaining = null;
      }
      this.syncProgress();
      this._demoTimer = setTimeout(() => {
        this._demoTimer = null;
        this._demoDueAt = null;
        if (!this.canDemoRun()) { this.syncDemo(); return; }
        if (this._gesture) this._gesture.autoStepped = true;
        this.step(1);
      }, Math.max(0, this._demoDueAt - performance.now()));
    }

    holdPresentation(owner, held) {
      if (held) {
        if (!this._presentationHolds.size && this._heldRemaining === null) {
          this._heldRemaining = this._demoDueAt === null ? HOLD_MS : Math.max(0, this._demoDueAt - performance.now());
        }
        this._presentationHolds.add(owner);
      } else {
        this._presentationHolds.delete(owner);
      }
      this.syncDemo();
    }

    suspendDemo(paused) {
      this._viewerPaused = paused;
      this.syncDemo();
    }

    openZoom() {
      if (!this._shown) return;
      if (this._viewer?.openView) { this._viewer.openView(); return; }
      const owner = this.closest('[data-orion-mattress-product], [data-orion-topper-product]') || document.getElementById(this.dataset.owner);
      const dialog = owner?.querySelector('[data-orion-lightbox], [data-orion-topper-lightbox]');
      const target = dialog?.querySelector('[data-orion-lightbox-image], [data-orion-topper-lightbox-image]');
      const image = this._views[this._index]?.querySelector('img');
      if (!dialog || !target || !image || typeof dialog.showModal !== 'function') return;
      target.removeAttribute('srcset');
      target.src = image.dataset.fullSrc;
      target.alt = image.alt;
      this.suspendDemo(true);
      dialog.addEventListener('close', () => this.suspendDemo(false), { once: true, signal: this._abort.signal });
      dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
    }
  }
  customElements.define('orion-mattress-gallery', OrionMattressGallery);
})();
